import {
    E, T,
    balancesManager,
    positionsManager,
    ordersManager,
    httpClient
} from '../imports';


const emptyUserData: T.UserData = {
    userId: '',
    tosVersion: undefined,
    tosAcceptedAt: undefined,
    grants: [],
    lastVersionBadgeDismissed: '0'
}

const emptyUserPrefs: T.UserPrefs = {
    uiPrefs: {
        showCents: true,
        pnlDisplay: E.ValueDisplayType.DOLLAR,
        chainStrikesDisplayCount: 10
    },
    tradePrefs: {
        favoriteTickers: [],
        defaultOrderReference: E.PriceReference.PERCENT,
        defaultStopLossPercent: 20,
        defaultTakeProfitPercent: 20,
        defaultDTE: 1,
        positionSizing: {
            amount: 100,            // Value for $ Amount mode (0-999,999)
            percent: 10,            // Value for % Account mode (0-100)
            contracts: 1,           // Value for # Contracts mode (0-999,999)
            activeType: 'contracts' // Currently active sizing method
        },
    },
    miscPrefs: {
        nickname: undefined,
        discordAlertsEnabled: true
    }
};

/**
 * UserManager is a singleton that manages user data and preferences.
 * It handles authentication, user permissions, and user-modifiable preferences.
 */
class UserManager {

    // Construct with empty data
    data: T.UserData = emptyUserData;
    prefs: T.UserPrefs = emptyUserPrefs;

    isSyncingWithServer = false; // Flag to prevent context saves during server sync
    
    /** Whether the user has accepted the terms of service */
    get tosAccepted(): boolean {
        return (this.data.tosVersion ?? 0) > 0;
    }

    // User event callbacks
    onUserUpdated: ((data: T.UserData, prefs: T.UserPrefs) => void) | null = null;
    onDisconnected: (() => void) | null = null;


    /**
     * Attempts to load user data and preferences from server.
     * Should be called after Auth0 authentication.
     */
    async load(): Promise<void> {
        if (this.isSyncingWithServer) {
            console.warn('[User] load() called while already syncing with server. Aborting to prevent overlap.');
            return;
        }
        this.isSyncingWithServer = true;

        try {
            // Fetch both user data and preferences
            const response = await httpClient.getUser();
            this.data = response.userData;
            this.prefs = response.userPrefs;
            this.onUserUpdated?.(this.data, this.prefs);
        } catch (error: any) {
            console.log(error);
            throw new Error(`[User] Load FAIL: ${JSON.stringify(error.message)}`);
        } finally {
            this.isSyncingWithServer = false;
        }
    }

    setDiscordToggle(enabled: boolean) {
        const oldValue = this.prefs.miscPrefs.discordAlertsEnabled;
        this.prefs.miscPrefs.discordAlertsEnabled = enabled;
        console.log('[User] Updated discordAlertsEnabled from', oldValue, 'to', enabled);
        this.onUserUpdated?.(this.data, this.prefs);
    }

    /**
     * Save selected user data to the server.
     * @param options Flags for which data to save (userData, tradePrefs, uiPrefs, miscPrefs)
     */
    async saveUserData(options: {
        userData?: boolean;
        tradePrefs?: boolean;
        uiPrefs?: boolean;
        miscPrefs?: boolean;
    } = {}): Promise<void> {
        try {
            // Determine which endpoint to use based on what's being saved
            if (options.userData) {
                // UserData only goes to /userData endpoint (lastVersionBadgeDismissed)
                await httpClient.putUserData({
                    lastVersionBadgeDismissed: this.data.lastVersionBadgeDismissed
                });
            }

            // Build userPrefs payload
            const prefsPayload: any = {};
            if (options.tradePrefs) prefsPayload.tradePrefs = this.prefs.tradePrefs;
            if (options.uiPrefs) prefsPayload.uiPrefs = this.prefs.uiPrefs;
            if (options.miscPrefs) prefsPayload.miscPrefs = this.prefs.miscPrefs;

            if (Object.keys(prefsPayload).length > 0) {
                await httpClient.putUserPrefs(prefsPayload);
            }

            console.log('[User] User data saved to server');
        } catch (error: any) {
            console.error('[User] Failed to save user data:', error);
            throw error;
        }
    }

    /**
     * Disconnect from backend and clear all user data.
     */
    async disconnect(): Promise<void> {
        
        this.data = emptyUserData;
        this.prefs = emptyUserPrefs;

        // Disconnect all three managers
        balancesManager.disconnect();
        positionsManager.disconnect();
        ordersManager.disconnect();

        this.onDisconnected?.();
        console.log('[User] Disconnected and cleared user data');
    }
}

// Export singleton instance
export const userManager = new UserManager();
